import React, { useContext } from 'react';
import CountUp from 'react-countup';
import { PrismicRichText } from '@prismicio/react';
import { Trans } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { BREAKPOINT } from '@constants';
import { PageSlice, SliceDonationHeroDetails } from '@contracts';
import Colors from '../Colors';
import LinkButton from '../LinkButton';
import useDesktop from '../useDesktop';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';
import '@src/sass/sharp-grotesk-font.scss';

interface Props {
  slice: PageSlice;
}

const DonationHero: React.FC<Props> = ({ slice }) => {
  const { state } = useContext(Context);
  const { currency } = state;
  const sliceDetails = slice.primary as SliceDonationHeroDetails;
  const isMobile = !useDesktop(BREAKPOINT.tablet);
  const darkButton = sliceDetails?.dark_button;
  const darkText = sliceDetails?.dark_text;
  const alignTopMobile = isMobile && sliceDetails?.align_top_mobile;
  const currencySymbol = currency.symbol;
  const donationAmount = Number(sliceDetails.total_amount);
  const targetAmount = sliceDetails.target_amount;
  const counterStartModifier = 0.96;
  const countDuration = 40;
  const startAmount = donationAmount * counterStartModifier;

  return (
    <Wrapper>
      <BackgroundImage
        src={(isMobile ? sliceDetails.background_image_mobile?.url : sliceDetails.background_image?.url) ?? ''}
        alt={(isMobile ? sliceDetails.background_image_mobile?.alt : sliceDetails.background_image?.alt) ?? ''}
        role="presentation"
      />
      <ContentBlock $darkText={darkText} $alignTopMobile={alignTopMobile}>
        <TextWrapper>
          {sliceDetails?.overline && <Overline $variant={TextVariant.UpperText4}>{sliceDetails.overline}</Overline>}
          <DonationAmount>
            <CountUp prefix={currencySymbol} useEasing={true} start={startAmount} end={donationAmount} separator="," duration={countDuration} />
          </DonationAmount>
          <SubHeading>
            <Trans i18nKey="donation-hero.target" values={{ target: `${currencySymbol}${targetAmount}` }} />
          </SubHeading>
          <BodyText>
            <PrismicRichText field={sliceDetails.bodytext.richText} />
          </BodyText>
        </TextWrapper>
        {sliceDetails.button_url && (
          <LinkButton
            isInline
            buttonTextColor={darkButton ? Colors.cream : undefined}
            backgroundColor={darkButton ? Colors.navyBlue : undefined}
            to={sliceDetails?.button_url}
          >
            {sliceDetails.button_text_key}
          </LinkButton>
        )}
      </ContentBlock>
    </Wrapper>
  );
};

export default DonationHero;

const Wrapper = styled.div`
  width: 100%;
  height: 720px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${BREAKPOINT.mobileM}px) {
    height: 600px;
  }
`;

const BackgroundImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: bottom;
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const ContentBlock = styled.div<{ $darkText?: boolean; $alignTopMobile: boolean }>`
  width: 560px;
  max-width: 100%;
  margin: ${(props) => (props.$alignTopMobile ? '32px auto' : 'auto auto')};
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 32px;
  padding: 0 30px;
  text-align: center;
  position: relative;
  z-index: 2;
  color: ${(props) => (props.$darkText ? Colors.navyBlue : Colors.cream)};
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 16px;
  text-align: center;
  position: relative;
`;

const Overline = styled(Typography)`
  color: inherit;
`;

const DonationAmount = styled.div`
  font-size: 120px;
  line-height: 1;
  font-family: SharpGroteskBold;
  color: ${Colors.lilac};

  @media (max-width: ${BREAKPOINT.mobileL}px) {
    font-size: 72px;
  }
`;

const SubHeading = styled.p`
  color: inherit;
  ${TEXT_STYLES[TextVariant.Heading5]};

  @media (max-width: ${BREAKPOINT.tablet}px) {
    ${TEXT_STYLES[TextVariant.Heading6]};
  }
`;

const BodyText = styled.div`
  > * {
    color: inherit;
    ${TEXT_STYLES[TextVariant.BodyRegularMD]};
  }
`;
